import type { DraftCell, DraftFieldCatalog, DraftRow } from './draftTypes';

/**
 * `localStorage` codec for row drafts.
 *
 * ONE KEY PER ROW, never a blob. A blob of 300 rows would be re-serialised on
 * every keystroke, and one corrupt row would poison every other.
 */

export const DRAFT_STORAGE_VERSION = 1;
export const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

const PREFIX = 'predictions:draft';

/** The `localStorage` surface this module needs. */
export interface DraftStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  key(index: number): string | null;
  readonly length: number;
}

/**
 * `v` before `scope` on purpose: bumping the version is then a single prefix
 * scan, not one per scope.
 */
export function storageKeyFor(scope: string, rowKey: string): string {
  return `${PREFIX}:v${DRAFT_STORAGE_VERSION}:${scope}:${rowKey}`;
}

function versionPrefix(): string {
  return `${PREFIX}:v${DRAFT_STORAGE_VERSION}:`;
}

interface StoredCell {
  value: unknown;
  touched: boolean;
  touchedAt: number;
  failedAt: number | null;
}

interface StoredRow {
  v: number;
  scope: string;
  key: string;
  savedAt: number;
  cells: Record<string, StoredCell>;
}

/** A cell's own deadline. Per CELL, not per row — see `DraftCell.failedAt`. */
function expiresAt(cell: { touchedAt: number; failedAt: number | null }): number {
  return (cell.failedAt ?? cell.touchedAt) + DRAFT_TTL_MS;
}

function valueMatchesKind(
  value: unknown,
  spec: DraftFieldCatalog[string]
): boolean {
  switch (spec.kind) {
    case 'string':
      return typeof value === 'string';
    case 'boolean':
      return typeof value === 'boolean';
    case 'enum':
      return typeof value === 'string' && spec.values.includes(value);
    default:
      return false;
  }
}

/**
 * Everything below swallows its own failures.
 *
 * A draft is scratch. Private mode, a full quota, a half-written value from a
 * killed tab — none of them may take down the screen the operator trades on,
 * and none of them is worth a toast either.
 */
function safeRemove(storage: DraftStorage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    /* nothing to do about it */
  }
}

/**
 * Read one row. Returns `undefined` when there is nothing usable, having
 * already cleaned up whatever was unusable.
 *
 * Drops, in order of blast radius: an unparseable or version-mismatched row
 * (whole row); a row whose stored identity disagrees with its key (whole row);
 * a field the catalog no longer declares, or whose stored value no longer
 * matches its declared kind (that CELL only — this is the schema-drift path,
 * and a removed column must not cost the operator the rest of the row); a cell
 * past its TTL (that cell).
 *
 * There is deliberately no migration path. A draft lives at most 24 h, so a
 * migrator for one would be dead code the day it shipped: the version bump IS
 * the migration.
 */
export function readRow(
  storage: DraftStorage,
  scope: string,
  rowKey: string,
  catalog: DraftFieldCatalog,
  now: number
): DraftRow | undefined {
  const storageKey = storageKeyFor(scope, rowKey);
  let raw: string | null;
  try {
    raw = storage.getItem(storageKey);
  } catch {
    return undefined;
  }
  if (raw == null) return undefined;

  let parsed: StoredRow;
  try {
    parsed = JSON.parse(raw) as StoredRow;
  } catch {
    safeRemove(storage, storageKey);
    return undefined;
  }

  if (
    !parsed ||
    parsed.v !== DRAFT_STORAGE_VERSION ||
    parsed.scope !== scope ||
    parsed.key !== rowKey ||
    typeof parsed.cells !== 'object' ||
    parsed.cells == null
  ) {
    safeRemove(storage, storageKey);
    return undefined;
  }

  const cells: Record<string, DraftCell> = {};
  for (const [field, stored] of Object.entries(parsed.cells)) {
    const spec = catalog[field];
    if (!spec) continue;
    if (spec.persist === false) continue;
    if (!stored || typeof stored !== 'object') continue;
    if (typeof stored.touchedAt !== 'number') continue;
    const failedAt = typeof stored.failedAt === 'number' ? stored.failedAt : null;
    if (expiresAt({ touchedAt: stored.touchedAt, failedAt }) <= now) continue;
    if (!valueMatchesKind(stored.value, spec)) continue;
    cells[field] = {
      value: stored.value,
      touched: stored.touched === true,
      touchedAt: stored.touchedAt,
      failedAt
    };
  }

  if (Object.keys(cells).length === 0) {
    safeRemove(storage, storageKey);
    return undefined;
  }
  return { cells, version: 0 };
}

/**
 * Write one row, keeping only what is worth restoring.
 *
 * `confirmed` is never persisted: it is a few-hundred-millisecond override of
 * the ack→refetch window, and after a reload the query cache is authoritative.
 * A persisted one would be a stale claim about the server.
 *
 * Untouched cells are not persisted either — a value the operator never chose
 * is not worth surviving a refresh.
 */
export function writeRow(
  storage: DraftStorage,
  scope: string,
  rowKey: string,
  row: DraftRow | undefined,
  catalog: DraftFieldCatalog,
  now: number
): void {
  const storageKey = storageKeyFor(scope, rowKey);
  const cells: Record<string, StoredCell> = {};
  for (const [field, cell] of Object.entries(row?.cells ?? {})) {
    const spec = catalog[field];
    if (!spec || spec.persist === false) continue;
    if (!cell.touched) continue;
    cells[field] = {
      value: cell.value,
      touched: true,
      touchedAt: cell.touchedAt,
      failedAt: cell.failedAt
    };
  }

  if (Object.keys(cells).length === 0) {
    safeRemove(storage, storageKey);
    return;
  }

  const payload: StoredRow = {
    v: DRAFT_STORAGE_VERSION,
    scope,
    key: rowKey,
    savedAt: now,
    cells
  };
  try {
    storage.setItem(storageKey, JSON.stringify(payload));
  } catch {
    /* quota / private mode — the in-memory store still holds the draft */
  }
}

/**
 * Sweep stale keys across the whole prefix: rows on an older version, and rows
 * whose every cell has expired. Without it the store grows across days of
 * operation on pairs that settled long ago.
 */
export function pruneStorage(
  storage: DraftStorage,
  catalog: DraftFieldCatalog,
  now: number
): void {
  let keys: string[];
  try {
    keys = Array.from({ length: storage.length }, (_, i) => storage.key(i)).filter(
      (k): k is string => typeof k === 'string' && k.startsWith(PREFIX)
    );
  } catch {
    return;
  }

  const current = versionPrefix();
  for (const key of keys) {
    if (!key.startsWith(current)) {
      safeRemove(storage, key);
      continue;
    }
    let raw: string | null;
    try {
      raw = storage.getItem(key);
    } catch {
      continue;
    }
    if (raw == null) continue;
    let parsed: StoredRow;
    try {
      parsed = JSON.parse(raw) as StoredRow;
    } catch {
      safeRemove(storage, key);
      continue;
    }
    const alive = Object.entries(parsed?.cells ?? {}).some(([field, cell]) => {
      if (!catalog[field]) return false;
      if (!cell || typeof cell.touchedAt !== 'number') return false;
      const failedAt = typeof cell.failedAt === 'number' ? cell.failedAt : null;
      return expiresAt({ touchedAt: cell.touchedAt, failedAt }) > now;
    });
    if (!alive) safeRemove(storage, key);
  }
}
